import { CHANNEL_MESSAGE_CREATED } from '@common/constants/webSockets'
import { isDmKind } from '@common/helpers/channelKind'
import {
  buildChannelNotificationTag,
  buildMessagePreview,
  isSilencedByChannelSettings,
  isPersonallyMentioned,
  isSilencedInGroupConversation,
} from '@common/helpers/channelNotifications'
import { buildChannelMessageLocation } from '@common/helpers/notificationRouting'
import { firstNameOf } from '@common/helpers/participantTitle'
import {
  ChannelMessageKind,
  IChannelListItem,
  IRecentConversationItem,
} from '@common/interfaces/channels'
import { Middleware } from '@reduxjs/toolkit'
import { isInAtlasModuleFrame } from '../helpers/atlasModuleFrame'
import {
  DesktopNotification,
  clearDesktopNotifications,
  showDesktopNotification,
} from '../helpers/desktopNotifications'
import { resolveAlertAcrossTabs, setLocalViewProvider } from '../helpers/messageAlertCoordinator'
import { playMessageSound } from '../helpers/messageSound'
import { MARK_READ_SUCCESS, MARKED_READ_ON_PAGE_LEAVE } from './channels/actions'
import { IChannelMessageView } from './channels/reducer'
import { conversationsBesideMain } from './channels/selectors'
import { isMessageBannerEnabled, muteMessageSound } from './profile/selectors'
import { IRootState } from './reducer'
import { IMessageToast, pushMessageToast } from './ui/actions'

/**
 * Sound, in-app toast and desktop notification for incoming messages.
 *
 * The three surfaces divide by where the user's attention is:
 *
 * - **In-app toast** — Atlas is on screen but you're not in that conversation. Shown
 *   in the visible tab, because that is where you are looking.
 * - **OS banner** — raised for anything that alerts at all, once, from a single
 *   elected tab.
 * - **Sound** — likewise, once, from that same tab.
 *
 * The banner used to be withheld whenever any tab was on screen, to avoid saying the
 * same thing twice. That gate was cut because "on screen" is not "being looked at":
 * `visibilityState` reports a tab as visible when it is the active tab in its window,
 * even if that window is behind your editor or on a second monitor — so the toast was
 * being drawn where nobody was looking and the banner suppressed on that basis. The
 * banner is now unconditional and the duplicate is accepted; what makes that bearable
 * is that banners are tagged per conversation, so they replace rather than stack, and
 * are cleared on read below rather than left to be swept up by hand.
 *
 * A middleware rather than a hook because the decision needs the state as it was
 * when the event arrived — who is logged in, which conversation is open, that
 * channel's mute settings. A component subscribing to the store sees the unread
 * count after the fact and cannot tell one new message from a refetch.
 *
 * This path and server push overlap for *every* message, not just directed ones:
 * presence no longer suppresses anything server-side (see resolveRecipient in
 * services/channels/notifyChannelMessage.ts, where both gates were removed), so
 * an online reader can be reached by both. Both build their tag with
 * `buildChannelNotificationTag` and the OS collapses them into one banner — that
 * shared tag, not a presence check, is what keeps it to a single notification.
 *
 * Whether to alert at all is settled across every open tab before anything fires;
 * see helpers/messageAlertCoordinator.ts for why no single tab can decide it.
 */

interface ChannelMessageEvent {
  channelId: string
  message: IChannelMessageView
  /** A record's own conversation (a report's), read on that record's page. */
  isRecordScoped?: boolean
}

interface AlertContent {
  shouldPlaySound: boolean
  /** False only when this browser has not granted notification permission. */
  shouldNotify: boolean
  notification: DesktopNotification
  /** The in-app toast, shown alongside the banner when this tab is on screen. */
  toast: IMessageToast
}

const isChannelMessageEvent = (data: unknown): data is ChannelMessageEvent => {
  if (!data || typeof data !== 'object') return false
  const candidate = data as Partial<ChannelMessageEvent>
  return typeof candidate.channelId === 'string' && !!candidate.message
}

const isMentioned = (message: IChannelMessageView, currentUserId: string): boolean =>
  message.mentions?.some((mention) => mention.id === currentUserId) ?? false

/**
 * What to call the conversation in the banner.
 *
 * `name` is already the resolved title for both kinds — the server titles a DM
 * from its other participants before it ever reaches the client (see
 * services/channels/List.ts). Re-deriving it here would mean a notification that
 * disagrees with the sidebar the user is about to click.
 */
const buildChannelLabel = (
  conversation: IRecentConversationItem | undefined,
  channel: IChannelListItem | undefined,
): string => {
  // A conversation first: `channels` holds named channels and ungrouped DMs only,
  // so every conversation in a channel and every Arc chat missed it entirely and
  // fell through to the generic name — which, once conversations became where all
  // the talking happens, meant most banners just said "Atlas".
  if (conversation) {
    return conversation.parentChannelName
      ? `#${conversation.parentChannelName} · ${conversation.name}`
      : conversation.name
  }
  if (!channel) return 'Atlas'
  const name = channel.name ?? channel.slug
  if (!name) return 'Atlas'
  return channel.slug ? `#${name}` : name
}

/**
 * A person talking to the reader rather than a group's thread — what the toast
 * and the banner call out, so a DM can't be lost among group traffic.
 */
const isDirectMessage = (
  conversation: IRecentConversationItem | undefined,
  channel: IChannelListItem | undefined,
): boolean => {
  const kind = conversation?.kind ?? channel?.kind
  return !!kind && isDmKind(kind) && !conversation?.isArc
}

/**
 * Everything about this message that does not depend on the other tabs. Null when
 * the message can never alert regardless of what any other tab is doing.
 */
const resolveAlertContent = (
  state: IRootState,
  { channelId, message, isRecordScoped }: ChannelMessageEvent,
): AlertContent | null => {
  if (isRecordScoped) return null
  const channelsState = state.channels
  const currentUserId = channelsState?.currentUserId ?? null

  if (!currentUserId || message.senderUserId === currentUserId) return null
  // Joins, topic changes and Arc's own turns are not "someone messaged you".
  if (message.kind !== ChannelMessageKind.Message) return null

  const channel = channelsState?.channels?.find((item) => item.id === channelId)
  const conversation = channelsState?.recentConversations?.items?.find(
    (item) => item.id === channelId,
  )
  const isMention = isMentioned(message, currentUserId)
  const isPersonalMention = isPersonallyMentioned(message.mentions, currentUserId)
  // A thread is never in `channels` (that list is groups and legacy DMs), so its
  // own row is where its mute lives — when the payload carries it: a row from a
  // server behind this client has no level, which would otherwise read as muted.
  const threadSettings = conversation?.notificationLevel ? conversation : undefined
  // A group's thread alerts by the group's level; see isSilencedInGroupConversation.
  const isSilenced = conversation?.parentChannelId
    ? isSilencedInGroupConversation({
        threadSettings,
        groupSettings: conversation.groupSettings,
        isMention,
        isPersonalMention,
        hasEngaged: conversation.hasParticipated,
      })
    : isSilencedByChannelSettings(channel ?? threadSettings, isMention, isPersonalMention)
  // The server's list of every muted conversation, loaded row or not: a muted thread is
  // in no Active row, and a row built from a message alone carries no level to read.
  const isMutedOnServer =
    !isPersonalMention &&
    (channelsState?.recentConversations?.mutedConversationIds ?? []).includes(channelId)
  // Likewise the group level for a thread with no loaded row: Engagement or Mentions-only
  // threads the reader is not following speak up only for a mention.
  const isQuietOnServer =
    !conversation &&
    !isMention &&
    (channelsState?.recentConversations?.quietThreadIds ?? []).includes(channelId)
  if (isSilenced || isMutedOnServer || isQuietOnServer) return null

  const shouldPlaySound = !muteMessageSound(state)
  const canNotify =
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted' &&
    isMessageBannerEnabled(state)

  // Full name on the payload — the toast passes it to MemberAvatar, which needs both
  // words to derive two initials. The label itself is shortened where it renders.
  // 'Someone' rather than 'New message' as the fallback: it is what the rest of
  // Messages calls an unresolved sender, and a single token survives `firstNameOf`
  // intact where a two-word placeholder would be cut down to "New".
  const senderName = message.senderName ?? 'Someone'
  // The server resolves this: a nickname stays whole, a legal name is cut to its
  // first word. Falls back to `firstNameOf` for a payload written before the
  // field existed (a socket frame already in flight across a deploy).
  const senderShortName = message.senderShortName ?? firstNameOf(senderName)
  const channelLabel = buildChannelLabel(conversation, channel)
  const isDirect = isDirectMessage(conversation, channel)
  const preview = buildMessagePreview(message, 'sent a message')

  return {
    shouldPlaySound,
    shouldNotify: canNotify,
    notification: {
      title: isDirect
        ? `${senderShortName} · Direct message · ${channelLabel}`
        : `${senderShortName} · ${channelLabel}`,
      body: preview,
      tag: buildChannelNotificationTag({ channelId, recipientUserId: currentUserId, isMention }),
      // The same deep link the notification entity produces, so a banner raised
      // here lands where the bell, the page and the server's own push land.
      link: buildChannelMessageLocation({ channelId, messageId: message.id }),
    },
    toast: {
      messageId: message.id,
      channelId,
      senderUserId: message.senderUserId,
      senderName,
      senderShortName,
      channelLabel,
      preview,
    },
  }
}

/**
 * Take down this conversation's banners once its messages have been read.
 *
 * Both tags, because a channel can hold one banner for ordinary messages and another
 * for mentions, and reading the channel deals with both. Fired on the same action
 * that clears the unread badge, so reading on a phone or in another tab takes the
 * banner down here too.
 */
const clearChannelNotifications = (state: IRootState, channelId: string | undefined): void => {
  const currentUserId = state.channels?.currentUserId
  if (!currentUserId || !channelId) return

  void clearDesktopNotifications(
    [false, true].map((isMention) =>
      buildChannelNotificationTag({ channelId, recipientUserId: currentUserId, isMention }),
    ),
  )
}

const messageAlertsMiddleware = (): Middleware<object, IRootState> => (store) => {
  const readLocalView = () => ({
    activeChannelId: store.getState().channels?.activeChannelId ?? null,
    splitChannelIds: conversationsBesideMain(store.getState()),
    isVisible: document.visibilityState === 'visible',
    isFocused: document.hasFocus(),
  })

  // The coordinator answers peers' broadcasts outside of any action, so it needs to
  // be able to ask what this tab is looking at rather than be told once.
  setLocalViewProvider(readLocalView)

  return (next) => (action: { type?: string; data?: unknown; params?: { channelId?: string } }) => {
    if (action.type === MARK_READ_SUCCESS || action.type === MARKED_READ_ON_PAGE_LEAVE) {
      clearChannelNotifications(store.getState(), action.params?.channelId)
      return next(action)
    }

    // Read before `next` so the tab's view reflects the moment the message arrived,
    // not after the reducer has folded it in.
    // The AIM page around a module-panel frame raises this message's alerts itself.
    if (
      action.type !== CHANNEL_MESSAGE_CREATED ||
      !isChannelMessageEvent(action.data) ||
      isInAtlasModuleFrame()
    ) {
      return next(action)
    }

    const { channelId, message } = action.data
    const content = resolveAlertContent(store.getState(), action.data)

    if (content) {
      const localView = readLocalView()

      // Async, so the reducer is never held up waiting on the other tabs.
      void resolveAlertAcrossTabs(message.id, channelId, localView).then((verdict) => {
        // The in-app toast goes where the user is looking, so it is gated on *this*
        // tab being visible rather than on winning the election — the executor may
        // well be a hidden tab, where a toast would be shown to nobody.
        if (localView.isVisible) store.dispatch(pushMessageToast(content.toast))

        // A conversation already open and in front of the reader has said its
        // piece with the toast. Sound and a banner on top of that announce a
        // message they are in the middle of reading.
        if (verdict.isOnScreen) return

        // Sound and the OS banner are once-per-message, so only the elected tab
        // performs them.
        if (!verdict.isExecutor) return

        const didPlaySound = content.shouldPlaySound && playMessageSound()

        // The banner carries the OS's own sound only when nothing else made one.
        // Muted means muted; a cue that actually sounded doesn't want a second noise
        // on top of it; but a cue that was dropped — no user gesture yet, or inside
        // the rate-limit floor — leaves the banner as the only thing that can be
        // heard, and silencing it there would lose the message entirely.
        if (content.shouldNotify) {
          void showDesktopNotification({
            ...content.notification,
            silent: !content.shouldPlaySound || didPlaySound,
          })
        }
      })
    }

    return next(action)
  }
}

export default messageAlertsMiddleware
